import React, { useState, useEffect, useCallback } from 'react';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, Plus, Trash2, Edit2, X, Server, AlertCircle } from 'lucide-react';
import { apiClient, KnownIP, CreateKnownIPRequest, UpdateKnownIPRequest } from '../../api/client';

interface ManageKnownIPsDialogProps {
    open: boolean;
    onClose: () => void;
    onUpdate: () => void;
}

// IPv4 validation regex
const IPV4_REGEX = /^(?:(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(?:25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

export const ManageKnownIPsDialog: React.FC<ManageKnownIPsDialogProps> = ({
    open,
    onClose,
    onUpdate,
}) => {
    const [knownIPs, setKnownIPs] = useState<KnownIP[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Form state
    const [showAddForm, setShowAddForm] = useState(false);
    const [editingIP, setEditingIP] = useState<KnownIP | null>(null);
    const [formData, setFormData] = useState({
        ip_address: '',
        name: '',
        description: '',
    });
    const [formError, setFormError] = useState<string | null>(null);
    const [saving, setSaving] = useState(false);

    // Load known IPs
    const loadKnownIPs = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const response = await apiClient.listKnownIPs();
            setKnownIPs(response.known_ips);
        } catch (err: any) {
            setError(err.message || 'Failed to load known IPs');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (open) {
            loadKnownIPs();
        }
    }, [open, loadKnownIPs]);

    // Reset form
    const resetForm = () => {
        setFormData({ ip_address: '', name: '', description: '' });
        setFormError(null);
        setShowAddForm(false);
        setEditingIP(null);
    };

    // Validate form
    const validateForm = (): string | null => {
        if (!editingIP && !formData.ip_address.trim()) {
            return 'IP address is required';
        }
        if (!editingIP && !IPV4_REGEX.test(formData.ip_address.trim())) {
            return 'Invalid IPv4 address format';
        }
        if (!formData.name.trim()) {
            return 'Name is required';
        }
        if (formData.name.length > 100) {
            return 'Name must be 100 characters or less';
        }
        if (formData.description.length > 500) {
            return 'Description must be 500 characters or less';
        }
        return null;
    };

    // Handle add/update
    const handleSubmit = async () => {
        const validationError = validateForm();
        if (validationError) {
            setFormError(validationError);
            return;
        }

        setSaving(true);
        setFormError(null);

        try {
            if (editingIP) {
                // Update existing
                const updateReq: UpdateKnownIPRequest = {
                    name: formData.name.trim(),
                    description: formData.description.trim(),
                };
                await apiClient.updateKnownIP(editingIP.id, updateReq);
            } else {
                // Create new
                const createReq: CreateKnownIPRequest = {
                    ip_address: formData.ip_address.trim(),
                    name: formData.name.trim(),
                    description: formData.description.trim() || undefined,
                };
                await apiClient.createKnownIP(createReq);
            }

            resetForm();
            await loadKnownIPs();
            onUpdate(); // Trigger topology refresh
        } catch (err: any) {
            setFormError(err.message || 'Failed to save known IP');
        } finally {
            setSaving(false);
        }
    };

    // Handle delete
    const handleDelete = async (id: string) => {
        if (!window.confirm('Are you sure you want to delete this known IP?')) {
            return;
        }

        try {
            await apiClient.deleteKnownIP(id);
            await loadKnownIPs();
            onUpdate(); // Trigger topology refresh
        } catch (err: any) {
            setError(err.message || 'Failed to delete known IP');
        }
    };

    // Start editing
    const startEdit = (ip: KnownIP) => {
        setEditingIP(ip);
        setFormData({
            ip_address: ip.ip_address,
            name: ip.name,
            description: ip.description || '',
        });
        setShowAddForm(true);
        setFormError(null);
    };

    return (
        <Dialog open={open} onOpenChange={(open) => !open && onClose()}>
            <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Server className="h-5 w-5 text-zinc-500" />
                        Manage Known IPs
                    </DialogTitle>
                    <DialogDescription>
                        Add friendly names to external IP addresses. Known IPs appear as named nodes in the topology map instead of generic external IPs.
                    </DialogDescription>
                </DialogHeader>

                {error && (
                    <Alert variant="destructive">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>{error}</AlertDescription>
                    </Alert>
                )}

                {/* Add/Edit Form */}
                {showAddForm ? (
                    <Card className="p-4 border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-950/20">
                        <div className="space-y-4">
                            <div className="flex justify-between items-center">
                                <h4 className="font-medium">
                                    {editingIP ? 'Edit Known IP' : 'Add Known IP'}
                                </h4>
                                <Button variant="ghost" size="sm" onClick={resetForm}>
                                    <X className="h-4 w-4" />
                                </Button>
                            </div>

                            {formError && (
                                <Alert variant="destructive">
                                    <AlertCircle className="h-4 w-4" />
                                    <AlertDescription>{formError}</AlertDescription>
                                </Alert>
                            )}

                            <div className="grid gap-4">
                                <div className="space-y-2">
                                    <Label htmlFor="ip_address">IP Address (IPv4)</Label>
                                    <Input
                                        id="ip_address"
                                        placeholder="e.g., 192.168.1.100"
                                        value={formData.ip_address}
                                        onChange={(e) => setFormData({ ...formData, ip_address: e.target.value })}
                                        disabled={!!editingIP} // Can't change IP when editing
                                        className={editingIP ? 'bg-muted' : ''}
                                    />
                                    {editingIP && (
                                        <p className="text-xs text-muted-foreground">IP address cannot be changed after creation</p>
                                    )}
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="name">Name *</Label>
                                    <Input
                                        id="name"
                                        placeholder="e.g., Production Database Server"
                                        value={formData.name}
                                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                        maxLength={100}
                                    />
                                    <p className="text-xs text-muted-foreground">{formData.name.length}/100</p>
                                </div>

                                <div className="space-y-2">
                                    <Label htmlFor="description">Description (optional)</Label>
                                    <Textarea
                                        id="description"
                                        placeholder="Add a description to help your team understand this IP..."
                                        value={formData.description}
                                        onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                        maxLength={500}
                                        rows={3}
                                    />
                                    <p className="text-xs text-muted-foreground">{formData.description.length}/500</p>
                                </div>
                            </div>

                            <div className="flex justify-end gap-2">
                                <Button variant="outline" onClick={resetForm}>Cancel</Button>
                                <Button onClick={handleSubmit} disabled={saving}>
                                    {saving ? (
                                        <>
                                            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                            Saving...
                                        </>
                                    ) : (
                                        editingIP ? 'Update' : 'Add'
                                    )}
                                </Button>
                            </div>
                        </div>
                    </Card>
                ) : (
                    <Button
                        variant="outline"
                        onClick={() => setShowAddForm(true)}
                        className="w-full border-dashed border-zinc-300 text-zinc-600 hover:bg-zinc-50 dark:border-zinc-700 dark:text-zinc-400 dark:hover:bg-zinc-900/30"
                    >
                        <Plus className="h-4 w-4 mr-2" />
                        Add Known IP
                    </Button>
                )}

                {/* List of Known IPs */}
                <div className="space-y-2">
                    <h4 className="font-medium text-sm text-muted-foreground">
                        Known IPs ({knownIPs.length})
                    </h4>

                    {loading ? (
                        <div className="flex justify-center py-8">
                            <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
                        </div>
                    ) : knownIPs.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <Server className="h-12 w-12 mx-auto mb-2 opacity-20" />
                            <p>No known IPs configured yet.</p>
                            <p className="text-sm">Add IP addresses that your resources communicate with to see them as named nodes in the topology.</p>
                        </div>
                    ) : (
                        <div className="space-y-2">
                            {knownIPs.map((ip) => (
                                <Card key={ip.id} className="p-3 hover:shadow-sm transition-shadow">
                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium">{ip.name}</span>
                                                <Badge variant="outline" className="text-xs font-mono border-zinc-300 text-zinc-600 dark:border-zinc-600 dark:text-zinc-400">
                                                    {ip.ip_address}
                                                </Badge>
                                            </div>
                                            {ip.description && (
                                                <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                                                    {ip.description}
                                                </p>
                                            )}
                                            <p className="text-xs text-muted-foreground mt-1">
                                                Added {new Date(ip.created_at).toLocaleDateString()}
                                            </p>
                                        </div>
                                        <div className="flex gap-1">
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => startEdit(ip)}
                                                className="h-8 w-8 p-0"
                                            >
                                                <Edit2 className="h-4 w-4" />
                                            </Button>
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleDelete(ip.id)}
                                                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ManageKnownIPsDialog;
